/**
 * OTIF Settings Tab Component
 * Handles OTIF calculation settings, tolerance windows, and score mappings
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  RotateCcw,
  Clock,
  AlertTriangle,
  Info,
  Settings2,
} from 'lucide-react';
import {
  SupplierPerformanceSettings,
} from '../../../lib/api';
import { PerformanceFormData } from './types';
import { useAuth } from '../../../contexts/AuthContext';
import { OTIFGradeThresholdsSection } from './OTIFGradeThresholdsSection';
import { useOTIFGradeThresholds } from './useOTIFGradeThresholds';
import { ScoreGradeBadge } from './ScoreGradeBadge';
import { KpiTargetValuesCard } from './KpiTargetValuesCard';

interface OTIFSettingsTabProps {
  settings: SupplierPerformanceSettings | null;
  formData: PerformanceFormData;
  /** CR-6267: OTIF colour band state, owned by useOTIFGradeThresholds */
  otifGrades: ReturnType<typeof useOTIFGradeThresholds>;
  onInputChange: (field: string, value: string | number | boolean) => void;
  onResetOTIFMappings: () => void;
}

export function OTIFSettingsTab({
  settings,
  formData,
  otifGrades,
  onInputChange,
  onResetOTIFMappings,
}: OTIFSettingsTabProps) {
  // CR-6543 r3: the fallback firm window is a staff-only setting — visible
  // to admins, editable only when the Django user carries is_staff /
  // is_superuser (the backend enforces the same rule on PATCH).
  const { user } = useAuth();
  const canEditFirmDays = Boolean(user?.is_staff || user?.is_superuser);
  return (
    <div className="space-y-4">
      {/* Delivery Metric Mode */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            <CardTitle>Delivery Metric Mode</CardTitle>
          </div>
          <CardDescription>
            Choose which delivery performance metrics to calculate and display across the platform.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {([
              { value: 'both', label: 'Both OTIF and OTD', desc: 'Calculate and display both On-Time In-Full and On-Time Delivery metrics' },
              { value: 'otif_only', label: 'OTIF Only', desc: 'Only calculate and display On-Time In-Full. OTD charts and KPIs will be hidden.' },
              { value: 'otd_only', label: 'OTD Only', desc: 'Only calculate and display On-Time Delivery. OTIF charts and KPIs will be hidden.' },
            ] as const).map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  formData.delivery_metric_mode === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <input
                  type="radio"
                  name="delivery_metric_mode"
                  value={option.value}
                  checked={formData.delivery_metric_mode === option.value}
                  onChange={() => onInputChange('delivery_metric_mode', option.value)}
                  className="h-4 w-4"
                />
                <div>
                  <span className="font-medium">{option.label}</span>
                  <p className="text-sm text-muted-foreground">{option.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <KpiTargetValuesCard formData={formData} onInputChange={onInputChange} />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle>OTIF Calculation Settings</CardTitle>
          </div>
          <CardDescription>
            Configure how On-Time In-Full (OTIF) is calculated for supplier deliveries.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* OTIF Tolerance Window */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Delivery Tolerance Window
            </h3>
            <p className="text-sm text-muted-foreground">
              Define the acceptable delivery window for a delivery to count as "on time".
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="otif_days_early_allowed">Days Early Allowed</Label>
                <Input
                  id="otif_days_early_allowed"
                  type="number"
                  min="0"
                  value={formData.otif_days_early_allowed}
                  onChange={(e) => onInputChange('otif_days_early_allowed', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Deliveries up to {formData.otif_days_early_allowed} days early will count as OTIF
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otif_days_late_allowed">Days Late Allowed</Label>
                <Input
                  id="otif_days_late_allowed"
                  type="number"
                  min="0"
                  value={formData.otif_days_late_allowed}
                  onChange={(e) => onInputChange('otif_days_late_allowed', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Deliveries up to {formData.otif_days_late_allowed} day(s) late will count as OTIF
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="arrears_buffer_days">Arrears Buffer (days)</Label>
                <Input
                  id="arrears_buffer_days"
                  type="number"
                  min="0"
                  value={formData.arrears_buffer_days}
                  onChange={(e) => onInputChange('arrears_buffer_days', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  A schedule is only counted as arrears once it is more than {formData.arrears_buffer_days} day(s) past due. Applies to the dashboard &amp; forecast arrears.
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="default_firm_days">Default Firm Window (days)</Label>
                  <Badge variant="outline" className="text-[10px] uppercase">Staff only</Badge>
                </div>
                <Input
                  id="default_firm_days"
                  type="number"
                  min="0"
                  max="365"
                  value={formData.default_firm_days}
                  disabled={!canEditFirmDays}
                  onChange={(e) => onInputChange('default_firm_days', parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Schedule lines for parts without a configured firm window are treated as firm for the next {formData.default_firm_days} day(s); beyond that they count as forecast. Drives the Scheduled Dashboard, forecast export and schedule list splits.
                </p>
              </div>
            </div>
          </div>

          {/* Arrears Setting */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Arrears Handling
            </h3>
            {/* DORMANT — not wired; see METRIC-DEFINITIONS W3-T2 (wave 3a). The
                "Include Arrears in OTIF" control is retired: arrears are always
                in the denominator, counted once. The column stays; nothing reads it. */}
            <p className="text-sm text-muted-foreground">
              Overdue open PO lines (arrears) are always counted in the OTIF denominator, once each.
            </p>
          </div>

          {/* OTIF Grade Thresholds (CR-6267) */}
          <OTIFGradeThresholdsSection
            bands={settings?.otif_grade_thresholds ?? []}
            editingBand={otifGrades.editingBand}
            bandEditData={otifGrades.bandEditData}
            savingBand={otifGrades.savingBand}
            showAddBand={otifGrades.showAddBand}
            newBandData={otifGrades.newBandData}
            addingBand={otifGrades.addingBand}
            resettingBands={otifGrades.resettingBands}
            onSetShowAddBand={otifGrades.setShowAddBand}
            onResetBands={otifGrades.handleResetBands}
            onEditBand={otifGrades.handleEditBand}
            onCancelEditBand={otifGrades.handleCancelEditBand}
            onSaveBand={otifGrades.handleSaveBand}
            onBandFieldChange={otifGrades.handleBandFieldChange}
            onAddBand={otifGrades.handleAddBand}
            onDeleteBand={otifGrades.handleDeleteBand}
            onNewBandFieldChange={otifGrades.handleNewBandFieldChange}
          />

          {/* OTIF Formula Info */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span className="font-medium">OTIF Calculation Formula</span>
            </div>
            <code className="block text-sm p-2 bg-background rounded border">
              OTIF % = Passed PO Lines / (Closed PO Lines + Arrears) x 100
            </code>
            <p className="text-sm text-muted-foreground">
              A PO line is marked as OTIF=1 when the delivery is received within the tolerance window
              ({formData.otif_days_early_allowed} days early to {formData.otif_days_late_allowed} day(s) late) and the full quantity is delivered.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* OTIF Score Mappings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>OTIF Score Mappings</CardTitle>
              <CardDescription>
                Map OTIF percentages to score points for rating calculations.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onResetOTIFMappings}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settings?.otif_score_mappings && settings.otif_score_mappings.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-4 text-sm font-medium text-muted-foreground pb-2 border-b">
                <div>Min OTIF %</div>
                <div>Max OTIF %</div>
                <div>Score Points</div>
                <div>Grade</div>
              </div>
              {settings.otif_score_mappings.map((mapping) => (
                <div key={mapping.id} className="grid grid-cols-4 gap-4 items-center py-2">
                  <div>{mapping.min_otif_percent}%</div>
                  <div>{mapping.max_otif_percent}%</div>
                  <div className="font-medium">{mapping.mapped_score} pts</div>
                  <div>
                    {/* CR-6269: the grade these points earn on the Supplier
                        Grade Thresholds tab — not a fixed Excellent/Good/Poor. */}
                    <ScoreGradeBadge score={mapping.mapped_score} bands={settings.rating_thresholds} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No OTIF score mappings configured. Click "Reset to Defaults" to create default mappings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
